// paymentReceiptPremium.ts

/**
 * World-Class Premium Payment Receipt Template
 * A sophisticated, high-end receipt design suitable for premium organizations
 */
export const paymentReceiptPremium = `
<mjml>
  <mj-head>
    <mj-title>Premium Payment Receipt | Copilotly</mj-title>
    <mj-preview>Your payment has been successfully processed. Here's your premium receipt.</mj-preview>
    <mj-attributes>
      <mj-all font-family="Arial, sans-serif" />
      <mj-text font-size="16px" color="#333333" line-height="1.5" />
      <mj-section padding="0px" />
    </mj-attributes>
    <mj-style>
      .premium-container { border-radius: 12px; overflow: hidden; box-shadow: 0 4px 16px rgba(0,0,0,0.08); }
      .premium-heading { border-bottom: 1px solid #f0f0f0; }
      .premium-success { display: inline-block; background-color: #DFF3E7; color: #1F7B4D; border-radius: 30px; padding: 6px 16px; font-weight: 500; font-size: 14px; }
      .premium-summary { background-color: #f9f9f9; border-radius: 8px; border: 1px solid #f0f0f0; }
      .premium-table { width: 100%; }
      .premium-table th { text-align: left; font-weight: 500; color: #888; font-size: 14px; padding: 8px 0; text-transform: uppercase; letter-spacing: 0.5px; }
      .premium-table td { padding: 12px 0; border-top: 1px solid #f0f0f0; }
      .premium-amount { font-family: 'Courier New', monospace; font-weight: 700; font-size: 18px; color: #1F7B4D; }
      .premium-border-top { border-top: 3px solid #f0f0f0; }
      .premium-total { font-size: 18px; font-weight: bold; }
      .premium-badge { display: inline-block; padding: 3px 8px; border-radius: 4px; font-size: 12px; font-weight: 500; background-color: #eef7ff; color: #0070f3; }
      .premium-badge-border { border-left: 4px solid #0070f3; padding-left: 12px; }
      .premium-method-icon { vertical-align: middle; margin-right: 8px; }
      .premium-secure { color: #767676; font-size: 13px; }
      .premium-footer-link { color: #555; text-decoration: underline; font-size: 14px; }
      .premium-divider { height: 1px; background-color: #f0f0f0; width: 100%; }
      .logo-container { text-align: center; padding: 40px 0 30px; }
      .premium-receipt-number { font-family: 'Courier New', monospace; letter-spacing: 1px; font-size: 14px; color: #888; font-weight: normal; }
      .premium-watermark { position: absolute; top: 0; right: 0; opacity: 0.03; width: 300px; height: 300px; z-index: -1; }
      .premium-top-ribbon { height: 6px; background: linear-gradient(to right, #336633, #99CC66); width: 100%; }
      @media (max-width: 480px) {
        .premium-mobile { display: block; width: 100%; }
      }
      
      /* Add these styles specifically for PDF rendering */
      @media print {
        .premium-container { 
          box-shadow: none !important; 
          page-break-inside: avoid;
        }
        img { 
          page-break-inside: avoid; 
          max-width: 100% !important;
        }
        table { 
          page-break-inside: avoid; 
        }
        .premium-top-ribbon { 
          height: 6px !important; 
          background: #336633 !important; 
          width: 100% !important;
        }
        .premium-success { 
          background-color: #DFF3E7 !important; 
          color: #1F7B4D !important; 
        }
        
        /* Force images to display */
        img[src^="https://bsauh-uae.s3.ap-south-1.amazonaws.com"] {
          display: block !important;
          visibility: visible !important;
        }
      }
    </mj-style>
  </mj-head>
  <mj-body background-color="#f5f5f5">
    <!-- Main Receipt Container -->
    <mj-wrapper padding="20px 0" background-color="#f5f5f5">
      <mj-section background-color="#ffffff" css-class="premium-container" padding="0">
        <!-- Top Accent Bar -->
        <mj-column padding="0">
          <mj-raw>
            <div class="premium-top-ribbon"></div>
          </mj-raw>
        </mj-column>
      </mj-section>
      
      <mj-section background-color="#ffffff" css-class="premium-container" padding="0" padding-top="5px">
        <!-- Logo Section -->
        <mj-column>
          <mj-raw>
            <div class="logo-container">
              <img src="https://bsauh-uae.s3.ap-south-1.amazonaws.com/bsauh-gallery-bucket/logo.png" alt="Copilotly Logo" width="150px" style="margin-bottom: 15px;" />
            </div>
          </mj-raw>
          
          <!-- Receipt Title and Status -->
          <mj-text align="center" font-size="28px" font-weight="bold" color="#333" padding="0 25px 10px" css-class="premium-heading">
            Payment Receipt
            <div class="premium-receipt-number">№ BSA-{{transaction_id}}</div>
          </mj-text>
          <mj-text align="center" padding="10px 25px 30px" css-class="premium-heading">
            <div class="premium-success">✓ Payment Successful</div>
          </mj-text>
        </mj-column>
      </mj-section>
      
      <!-- Transaction Details -->
      <mj-section background-color="#ffffff" css-class="premium-container" padding-top="0">
        <mj-column padding="0 40px">
          <mj-text font-size="18px" font-weight="bold" color="#333" padding-bottom="15px">
            Transaction Details
          </mj-text>
            
          <mj-text css-class="premium-summary" padding="25px">
            <table style="width: 100%; border-collapse: collapse;">
              <tr>
                <td style="width: 50%; padding: 6px 0; color: #555555; font-size: 14px;">Transaction ID</td>
                <td style="width: 50%; padding: 6px 0; font-weight: 600; text-align: right;">TXN-{{transaction_id}}</td>
              </tr>
              <tr>
                <td style="width: 50%; padding: 6px 0; color: #555555; font-size: 14px;">Transaction Date</td>
                <td style="width: 50%; padding: 6px 0; text-align: right;">{{transaction_date}}</td>
              </tr>
              <tr>
                <td style="width: 50%; padding: 6px 0; color: #555555; font-size: 14px;">Payment Status</td>
                <td style="width: 50%; padding: 6px 0; text-align: right; color: #1F7B4D; font-weight: 600;">
                  <span style="display: inline-block; width: 8px; height: 8px; border-radius: 50%; background-color: #1F7B4D; margin-right: 6px;"></span>Completed
                </td>
              </tr>
              <tr>
                <td style="width: 50%; padding: 16px 0 6px; color: #555555; font-size: 14px;">Amount Paid</td>
                <td style="width: 50%; padding: 16px 0 6px; text-align: right; font-size: 24px; font-weight: bold; color: #1F7B4D;">AED {{amount_paid}}</td>
              </tr>
            </table>
          </mj-text>

          <!-- Payment Method -->
          <mj-spacer height="25px" />
          <mj-text css-class="premium-badge-border" padding-left="15px" padding-top="5px" padding-bottom="5px">
            <span class="premium-badge">PAYMENT METHOD</span>
          </mj-text>
          <mj-text padding-top="20px">
            <table style="width: 100%;">
              <tr>
                <td style="width: 48px; vertical-align: middle;">
                  <div style="width: 48px; height: 48px; border-radius: 8px; background-color: #f5f5f5; display: flex; align-items: center; justify-content: center; box-shadow: 0 2px 6px rgba(0,0,0,0.05);">
                    <img src="https://bsauh-uae.s3.ap-south-1.amazonaws.com/bsauh-gallery-bucket/logo.png" width="28" height="28" alt="{{payment_method}}" />
                  </div>
                </td>
                <td style="vertical-align: middle; padding-left: 16px;">
                  <div style="font-weight: 600; font-size: 16px;">{{payment_method}}</div>
                  <div style="color: #777; font-size: 14px; margin-top: 3px;">•••• •••• •••• {{last_four}}</div>
                </td>
              </tr>
            </table>
          </mj-text>
          
          <mj-spacer height="30px" />
          
          <!-- Purchase Details with Enhanced Design -->
          <mj-text font-size="18px" font-weight="bold" color="#333" padding-bottom="15px">
            Purchase Details
          </mj-text>
          
          <mj-table css-class="premium-table" cellpadding="10">
            <tr style="background-color: #f9f9f9;">
              <th style="width: 45%; padding: 12px 10px; border-radius: 5px 0 0 5px;">Item</th>
              <th style="width: 20%; padding: 12px 10px;">Qty</th>
              <th style="width: 15%; text-align: right; padding: 12px 10px;">Price</th>
              <th style="width: 20%; text-align: right; padding: 12px 10px; border-radius: 0 5px 5px 0;">Amount</th>
            </tr>
            <tr>
              <td style="padding: 16px 10px;">
                <div style="font-weight: 600;">{{event_name}}</div>
                <div style="color: #777; font-size: 14px; margin-top: 4px;">{{ticket_type}}</div>
              </td>
              <td style="padding: 16px 10px;">{{quantity}}</td>
              <td style="text-align: right; padding: 16px 10px;">AED {{unit_price}}</td>
              <td style="text-align: right; padding: 16px 10px; font-weight: 600;">AED {{line_total}}</td>
            </tr>
          </mj-table>
          
          <mj-spacer height="10px" />
          
          <!-- Totals with Enhanced Design -->
          <mj-text padding="20px" css-class="premium-summary" align="right">
            <table style="width: 100%; border-collapse: collapse;">
              <tr>
                <td style="width: 80%; text-align: right; color: #555; padding: 5px 0;">Subtotal:</td>
                <td style="width: 20%; text-align: right; padding: 5px 0;">AED {{subtotal}}</td>
              </tr>
              <tr>
                <td style="width: 80%; text-align: right; color: #555; padding: 5px 0;">Tax (5% VAT):</td>
                <td style="width: 20%; text-align: right; padding: 5px 0;">AED {{tax_amount}}</td>
              </tr>
              <tr style="border-top: 2px solid #eee;">
                <td style="width: 80%; text-align: right; font-weight: bold; padding: 12px 0 5px; font-size: 16px;">Total:</td>
                <td style="width: 20%; text-align: right; font-weight: bold; padding: 12px 0 5px; font-size: 16px; color: #1F7B4D;">AED {{total_amount}}</td>
              </tr>
            </table>
          </mj-text>
          
          <!-- Secure Transaction Message with Enhanced Design -->
          <mj-text align="center" css-class="premium-secure" padding="20px 0 40px">
            <div style="display: inline-block; padding: 12px 20px; background-color: #f9f9f9; border-radius: 30px;">
              <img src="https://bsauh-uae.s3.ap-south-1.amazonaws.com/bsauh-gallery-bucket/logo.png" width="12" height="12" style="vertical-align: middle; margin-right: 8px;" /> 
              This transaction was processed securely with 256-bit encryption
            </div>
          </mj-text>
        </mj-column>
      </mj-section>
    </mj-wrapper>
  </mj-body>
</mjml>
`;

// Template metadata for the gallery
export default {
  id: "paymentReceiptPremium",
  name: "World-Class Premium Receipt",
  category: "transactional",
  purpose: "transactional" as const,
  description:
    "A sophisticated, high-end payment receipt with elegant styling, prominent logo placement, and refined visual elements suitable for premium organizations.",
  features: [
    "Prominent central logo placement",
    "Elegant visual design elements",
    "Premium typography and spacing",
    "Enhanced transaction details presentation",
    "Sophisticated payment method display",
    "Visually structured purchase summary",
    "Professional security indicator",
  ],
  subject: "Payment Receipt | Copilotly",
  subTitle: "Your payment has been successfully processed.",
  mjmlTemplate: paymentReceiptPremium,
  tags: ["transactional", "payment", "receipt", "premium"],
};
